import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ExternalLink } from "lucide-react";
import { api, apiError } from "../lib/api";
import { DetailsTab } from "../components/admin/DetailsTab";
import { BookFileTab } from "../components/admin/BookFileTab";
import { AppTab } from "../components/admin/AppTab";

const TABS = [
  { id: "details", label: "Book details" },
  { id: "bookfile", label: "Book file" },
  { id: "app", label: "The app" },
];

const BookMark = ({ className = "h-8 w-8" }) => (
  <svg viewBox="0 0 64 64" className={className} fill="none" aria-hidden="true">
    <rect x="17" y="10" width="32" height="44" rx="5" stroke="#D4A373" strokeWidth="4.5" />
    <line x1="25" y1="10" x2="25" y2="54" stroke="#D4A373" strokeWidth="4.5" />
    <line x1="31" y1="46" x2="43" y2="46" stroke="#E0A96D" strokeWidth="4" />
  </svg>
);

function LoginScreen({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/auth/login", { email, password });
      onSuccess(r.data);
    } catch (err) {
      setError(apiError(err, "Login failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B0C0E] px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-sm border border-[#262930] bg-[#121418] p-10">
        <BookMark className="h-10 w-10" />
        <h1 className="mt-6 font-display text-3xl text-[#F4F1EA]">The Studio</h1>
        <p className="mt-2 font-sans text-xs uppercase tracking-[0.25em] text-[#6E6B63]">Author access only</p>

        <label className="mt-8 block font-sans text-[10px] uppercase tracking-[0.25em] text-[#A39E93]" htmlFor="admin-email">
          Email
        </label>
        <input
          id="admin-email"
          data-testid="admin-login-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 w-full rounded-sm border border-[#3E4350] bg-[#0B0C0E] px-4 py-3 font-sans text-sm text-[#F4F1EA] outline-none focus:border-[#D4A373]"
        />
        <label className="mt-5 block font-sans text-[10px] uppercase tracking-[0.25em] text-[#A39E93]" htmlFor="admin-password">
          Password
        </label>
        <input
          id="admin-password"
          data-testid="admin-login-password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 w-full rounded-sm border border-[#3E4350] bg-[#0B0C0E] px-4 py-3 font-sans text-sm text-[#F4F1EA] outline-none focus:border-[#D4A373]"
        />
        {error && (
          <p data-testid="admin-login-error" className="mt-4 font-sans text-xs text-[#C85A32]">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={busy}
          className="mt-8 w-full rounded-full bg-[#D4A373] px-6 py-3 font-sans text-xs font-semibold uppercase tracking-[0.25em] text-[#0B0C0E] transition-colors hover:bg-[#E0A96D] disabled:opacity-50"
        >
          {busy ? "Opening…" : "Enter the studio"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [auth, setAuth] = useState(null);
  const [book, setBook] = useState(null);
  const [tab, setTab] = useState("details");

  const refreshBook = useCallback(() => {
    api.get("/book").then((r) => setBook(r.data)).catch(() => toast.error("Could not load book data"));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/auth/me");
        setAuth(r.data);
      } catch {
        try {
          await api.post("/auth/refresh");
          const r = await api.get("/auth/me");
          setAuth(r.data);
        } catch {
          setAuth(false);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (auth) refreshBook();
  }, [auth, refreshBook]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setAuth(false);
  };

  if (auth === null)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0C0E]">
        <span className="animate-pulse">
          <BookMark className="h-12 w-12" />
        </span>
      </div>
    );

  if (!auth) return <LoginScreen onSuccess={setAuth} />;

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-[#0B0C0E] font-sans text-[#F4F1EA]">
      <header className="border-b border-[#262930] bg-[#0E1013]">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4 sm:px-8">
          <span className="flex items-center gap-3">
            <BookMark className="h-7 w-7" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#A39E93]">The Studio</span>
          </span>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-2 rounded-full border border-[#3E4350] px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-[#A39E93] transition-colors hover:border-[#D4A373] hover:text-[#D4A373]"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              View site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-[#3E4350] px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-[#A39E93] transition-colors hover:border-[#C85A32] hover:text-[#C85A32]"
            >
              <LogOut className="h-3.5 w-3.5" />
              Log out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <div className="flex gap-2 border-b border-[#262930]">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 px-4 py-3 text-[11px] uppercase tracking-[0.25em] transition-colors ${
                tab === t.id ? "border-[#D4A373] text-[#F4F1EA]" : "border-transparent text-[#6E6B63] hover:text-[#A39E93]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="py-10">
          {!book ? (
            <p className="text-sm text-[#6E6B63]">Loading…</p>
          ) : tab === "details" ? (
            <DetailsTab meta={book.meta} onSaved={refreshBook} />
          ) : tab === "bookfile" ? (
            <BookFileTab />
          ) : (
            <AppTab meta={book.meta} />
          )}
        </div>
      </div>
    </div>
  );
}
